export type AddressType = {
  id: string;
  street: string;
  street_line_two: string;
  full_address: string;
  search_string: string;
  street_number: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  latitude: string;
  longitude: string;
  verified: boolean;
  created_at: Date;
  updated_at: Date;
  property_id: string | null;
  favorited: boolean;
};

export type PropertyFavoriteFlagType = {
  created_at: Date;
  id: string;
  property_id: string;
  updated_at: Date;
  user_id: string;
};

export type FormattedPropertyType = {
  index: number;
  property_id: string;
  id: string;
  favoriteStatus: boolean;
  wantedStatus?: boolean | null;
  full_address?: string;
  city?: string;
  zipcode?: string;
  state?: string | undefined;
  mlsStatus?: string;
  sqrft?: number;
  yearBuilt?: number;
  bedrooms?: number;
  bathrooms?: number;
  garage_spaces?: number;
  property_class?: string;
  county_code?: string;
  pool?: string;
  assessed_value: number | null;
  avm_result_id?: string | null;
  avm_result?: number | null;
  potential_equity?: number | null;
  clerk_number: number | string | null;
  created_at?: Date | null;
  cumulative_days_on_market?: number;
  data_source?: string;
  how_sold?: any;
  last_mls_id?: string | number;
  last_sold_date?: Date | null;
  last_sync_datetime?: Date | null;
  legal_description?: string;
  list_price?: number;
  lot_size_acres?: number;
  number_of_units?: number;
  on_market_date?: Date;
  property_listing_id?: string;
  recent_rehab?: any;
  sale_or_rent?: string;
  sqft?: number;
  status?: string;
  stories?: number;
  subdivision?: string;
  tax_id?: string;
  total_tax_amount?: number;
  updated_at?: Date;
  view_count?: number;
  year_built?: number;
  zillow_url?: string;
  list_price_per_sqft?: number;
  avm_price_per_sqft?: number;
  proposed_terms?: string[] | null;
  state_abbreviation?: string;
};

export interface PropertyDataProps {
  address: AddressType;
  assessed_value: number | null;
  avm_price_per_sqft: number;
  avm_result_id: string | null;
  bathrooms: number | null;
  bedrooms: number | null;
  clerk_number: number | string | null;
  county_code: string | null;
  created_at: Date | null;
  cumulative_days_on_market: number;
  data_source: string;
  garage_spaces: number;
  how_sold: any;
  id: string;
  last_mls_id: string | number; // ?
  last_sold_date: Date | null;
  last_sold_listing: LastSoldListing;
  last_sold_price: number | null;
  last_sync_datetime: Date | null;
  legal_description: string;
  list_price: number;
  list_price_per_sqft: number;
  list_sort_property_join: ListSortPropertyJoinType;
  lot_size_acres: number;
  most_recent_avm_result: any;
  number_of_units: number;
  on_market_date: Date;
  pool: string;
  potential_equity: number;
  property_class: string;
  property_favorite_flags: PropertyFavoriteFlagType[];
  property_listing_id: string;
  recent_rehab: any;
  sale_or_rent: string;
  sqft: number;
  status: string;
  stories: number;
  subdivision: string;
  tax_id: string;
  total_tax_amount: number;
  updated_at: Date;
  view_count: number;
  year_built: number;
  zillow_url: string;
  extraProps?: any;
  proposed_terms?: string[] | null;
}

export type PropertyType = {
  address: AddressType;
  id: string;
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  year_built: number;
  assessed_value: number;
  garage_spaces: number;
  number_of_units: number;
  tax_id: string;
  zillow_url: string;
  legal_description: string;
  clerk_number: string;
  property_class: string;
  county_code: string;
  pool: string;
  status: string;
  subdivision: string;
  property_favorite_flags: PropertyFavoriteFlagType[];
  list_sort_property_join: ListSortPropertyJoinType;
  proposed_terms?: string[] | null;
  // TODO: REIX-134 -> this should consume from a different context for the listview. Do this in listviewsort summary clean up ticket
  // Question to answer: is there a way to make it (or is it worth it) to have it only send back the list_joins associated to the incoming listsort request?
  //  So it does not need to be done on the front end
};

export type TitleProps = {
  title: string;
  subtitle: string;
  isLoggedIn: boolean;
};

export type BasicTableProps = {
  tableData: {}[];
  showID?: boolean;
};

export type DropdownProps = {
  optionsList: string[];
  title: string;
};

export type RadioButtonRowSelectProps = {
  title: string;
  subtitle?: string;
  options: string[];
  onSelect: (selected: string) => void;
  selected: string;
};

export type SearchDropdownSelectionValueProps = {
  key: string;
  subtitle?: string;
};

export type SearchDropdownProps = {
  initialValue: string;
  selections: Array<SearchDropdownSelectionValueProps>;
  onSelect: (selected: string) => void;
};

export type StepsProps = {
  id: number;
  name: string;
  href: string;
};

export type StepsBannerProps = {
  steps: Array<StepsProps>;
  activeStep: number;
  skipable?: boolean;
};

export type TableProps = {
  objectName: string;
  subtitle?: string;
  tableData: Array<Map<string, string>>;
  basePath: string;
  showID?: boolean;
};

export type LinkedPropertyMapperProps = {
  listUploadId: string;
  basePath: string;
};

export type PropertyMapperProps = {
  csvHeaders: string;
  existingMapping: string;
  mappableFieldsJson: string;
  formId: string;
};

export type ShowListUploadProps = {
  listUploadJson: string;
  propertyCount: number;
  mappableFieldsJson: string;
  basePath: string;
};

export type ListUploadTableProps = {
  listUploadsJson: string;
};

export type ValidateShowListUploadProps = {
  listUploadJson: string;
  mappableFieldsJson: string;
  validRowCount: number;
  errorRowCount: number;
  errorRowsJson: string;
  allRowsJson: string;
  basePath: string;
};

export type AddressProps = {
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
};

export type AddressTableProps = {
  addresses: AddressProps[];
};

export type MarkerProps = {
  lat: number;
  lng: number;
  text: string;
};

export type PropertyDetailsMarkerProps = {
  mapKey: string;
  lat: number;
  lng: number;
  address: AddressType;
  text: string;
  propertyId: string;
  selectedMarkerId: string;
  setMarkerAsOpened: Function;
  mapBoundingBox: MapBoundingBox;
};

export type MapBoundingBox = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

export type ListSortPropertyJoinType = {
  created_at: Date;
  id: string;
  list_sort_id: string;
  notes: string;
  order: number;
  property_id: string;
  updated_at: Date;
  wanted: boolean;
};

export type PropertiesVisualizerProps = {
  isMapView: boolean;
  listSortId: string;
};

export type PropertiesMapProps = {
  listSortId: string;
};

export type PropertiesViewAppProps = {
  mapKey: string;
  listSortId: string;
  isLoggedIn: boolean;
};

export type SortingSummaryProps = {
  showListSortSortSummary: boolean;
  setShowListSortSortSummary: React.Dispatch<React.SetStateAction<boolean>>;
  listSortId: string;
};

export type SaveSearchCriteriaModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  errorCreateSavedSearchCriteria: Error;
};

export type ListLineItemProps = {
  propertyId: string;
  property: PropertyType;
  toggleDisplaySummary: (propId) => void;
  fromListSortView: boolean;
  listSortId: string;
};

// incoming from the list sort property joins
export type ListSortPropertyJoins = {
  wanted: boolean;
  property_id: string;
  notes: string;
  order: number;
  created_at: Date;
  updated_at: Date;
  approach_id: string;
  estimated_profit: number;
  estimated_value: number;
  estimated_value_per_sqft: number;
  fix_costs: number;
  id: string;
  list_sort_id: string;
  max_bid: number;
  max_bid_per_sqft: number;
  roi: number;
  user_estimated_value: number;
  user_estimated_value_sqft: number;
  user_fixed_costs: number;
  user_input: string;
  valuations_option: string;
};

export type ListSortProps = {
  id: string;
  title: string;
  notes: string;
  list_upload_name: string;
  list_upload_id?: string | null;
  list_sort_property_joins: ListSortPropertyJoins[];
  created_at: Date;
  total_properties: number;
};

export type CreateListSortProps = {
  list_sort: {
    title: string;
    notes: string;
    list_upload: string;
  };
};

export type UpdateListSortProps = {
  list_sort: {
    title: string;
    notes: string;
  };
  listSortIdToEdit: string;
};

export type WantedStatusResponsePayloadType = {
  wanted: boolean;
  total_wanted_property_joins: number;
  total_unwanted_property_joins: number;
  total_sorted_property: number;
};

export type WantedStatusPayloadType = {
  wanted: boolean;
  notes: string;
  order: number;
};
export type WantedStatusPayloadType2 = {
  list_sort_property_join: {
    wanted: boolean;
    notes: string;
  };
};

export type LoadSearchCriteriaConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  criteria: string[];
  criteriaName: string;
};

export type DeleteSearchCriteriaConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (e: any) => void;
  criteriaTitle: string;
};

export type PropertiesSortSummaryProps = {
  showPropertiesSortSummary: boolean;
  setShowPropertiesSortSummary: React.Dispatch<React.SetStateAction<boolean>>;
};

export type NewListSortData = {
  name: string;
  notes: string;
  selectedListUpload: string;
};

export type EditListSortData = {
  name: string;
  notes: string;
};

export type CreateListSortFormDataProps = {
  title: string;
  notes: string;
  list_upload: string;
};

export type GetListSort = {
  listSortId: string;
};

export type GetPropertiesProps = {
  listSortId?: string;
  ransackObj?: any;
};

export type CreateListSortPropertyJoinProps = {
  requestBody: WantedStatusPayloadType2;
  listSortId: string;
  propertyId: string;
};

export type UpdateListSortPropertyJoinProps = CreateListSortPropertyJoinProps;

export type UseBulkListSortPropertyJoinProps = {
  listSortId: string;
  propertyIds: string[];
};
export type BulkOperationListSortPropertyJoinProps = {
  requestBody: BulkLSPJPayload;
  listSortId: string;
};
export type BulkLSPJPayload = {
  list_sort_property_join: {
    propertyIds: string[];
  };
};

export type UseCreateListSortPropertyJoinProps = {
  listSortId: string;
  propertyId: string;
  status: boolean;
};
export type UseUpdateListSortPropertyJoinProps =
  UseCreateListSortPropertyJoinProps;

export type DeleteListSortPropertyJoinProps = {
  listSortId: string;
  propertyId: string;
};

export type SearchParameterObject = {
  title?: string;
  notes?: string;
  list_upload?: string;
  list_sort_id?: string;
  tax_id?: string;
  zillow_url?: string;
  legal_description?: string;
  clerk_number?: string;
  property_class?: string;
  county_code?: string;
  pool?: boolean;
  status?: string;
  number_of_units?: number;
  subdivision?: string;
  bathrooms?: number;
  bedrooms?: number;
  garage_spaces?: number;
  sqft?: number;
  assessed_value?: number;
  photos?: string[];
  wanted_true?: boolean;
  wanted_false?: boolean;
  wanted_null?: boolean;
  proposed_terms?: string[] | null;
  // Add more properties as needed
};

export type SearchCriteriaType = {
  created_at: string;
  id: string;
  max_arv_estimate_value: number | null;
  max_assessed_value: number | null;
  max_bathrooms: number | null;
  max_bedrooms: number | null;
  max_garage_spaces: number | null;
  max_initial_equity: number | null;
  max_initial_loan_takeout: number | null;
  max_initial_loan_value: number | null;
  max_initial_ltv: number | null;
  max_number_of_units: number | null;
  max_sqft: number | null;
  max_stories: number | null;
  max_tax_appraisal_value: number | null;
  max_year_built: number | null;
  min_arv_estimate_value: number | null;
  min_assessed_value: number | null;
  min_bathrooms: number | null;
  min_bedrooms: number | null;
  min_garage_spaces: number | null;
  min_initial_equity: number | null;
  min_initial_loan_takeout: number | null;
  min_initial_loan_value: number | null;
  min_initial_ltv: number | null;
  min_number_of_units: number | null;
  min_sqft: number | null;
  min_stories: number | null;
  min_tax_appraisal_value: number | null;
  min_year_built: number | null;
  pool: any[]; // Replace 'any' with the actual type of the 'pool' property
  status: any[]; // Replace 'any' with the actual type of the 'status' property
  title: string;
  updated_at: string;
  user_id: string;
};

export type AvmComp = {
  acre_score: number;
  avm_result_id: string;
  bath_number_score: number;
  bed_number_score: number;
  city_score: number;
  created_at: string;
  distance: number;
  geo_build_score: number;
  id: string;
  original_order: number;
  pool_score: number;
  property: PropertyDataProps;
  property_id: string;
  property_value_at_time_of_calculation: number;
  recent_rehab_score: number;
  sale_type_score: number;
  score_comp_value: number;
  sold_date_score: number;
  sold_dollar_per_sqft: number;
  sqft_score: number;
  status_score: number;
  story_score: number;
  updated_at: string;
  weighted_dollar_per_sqft: number;
  year_build_score: number;
  zipcode_score: number;
};

export type AvmCompForMap = AvmComp & {
  isActive: boolean;
};

export type AvmResult = {
  active_avm: boolean;
  all_comp_avg_sold_price: number;
  all_comp_dollar_per_sqft: number;
  avg_comp_sqft: number;
  avg_weighted_dollar_per_sqft: number;
  avm_version: string;
  closest_avg_dollar_per_sqft: number;
  closest_avg_sold_price: number;
  confidence_value: number;
  created_at: Date;
  estimated_dollar_per_sqft: number;
  estimated_value: number;
  high_value: number;
  id: string;
  low_value: number;
  max_dollar_per_sqft: number;
  number_of_available_comps: number;
  potential_equity: number;
  property_id: string;
  time_of_calculation: Date;
  updated_at: Date;
};

export type LastSoldListing = {
  bedrooms: number;
  buying_agent_id: string;
  city_tax: any;
  close_date: Date;
  close_price: number;
  construction: string;
  county_property_tax_id: string;
  county_tax: any;
  created_at: Date;
  cumulative_days_on_market: number;
  data_source: string;
  days_on_market: number;
  full_bathrooms: number;
  garage_spaces: number;
  half_bathrooms: number;
  how_sold: string;
  id: string;
  legal_description: string;
  list_price: number;
  listing_agent_id: string;
  listing_status: string;
  lot_features: string[];
  lot_size_acres: number;
  number_of_units: number;
  occupancy_details: string[];
  occupant_type: string;
  off_market_date: Date;
  on_market_date: Date;
  pool_details: string[];
  price_per_sqft: number;
  property_class: number;
  property_id: string;
  property_type: string;
  proposed_terms: string[];
  recent_rehab: any;
  sale_or_rent: string;
  school_tax: any;
  seller_concessions: string;
  seller_consession_details: string[];
  sold_list_price: number;
  sold_original_price: number;
  sold_price_per_sqft: number;
  sold_sqft_acre: any;
  sqft: number;
  status_change_date: Date;
  stories: number;
  subdivision_name_legal: string;
  third_party_area_id: string;
  third_party_id: string;
  third_party_last_modified_date: Date;
  total_tax: number;
  updated_at: Date;
  year_build: number;
};

export type FinalDecisionMakingProperty = {
  propertyId: number;
  customListSortNumber: number;
  maxBid: string;
  maxBidValue?: number | null;
  repairEstimate: string;
  arvSelected: string;
  propertyListingId: number;
  profitOnMaxBid: string;
  roiPercent: string;
  approachName: string | undefined;
  address: string | undefined;
  sqFt: string | undefined;
  yearBuilt: number | undefined;
  bedrooms: number | undefined;
  bathrooms: number | undefined;
  acreage: string | undefined;
};

export type ListSortDetailsType = {
  id: string;
  title: string;
  notes: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  list_upload_id: string;
  wanted_items_count: number;
  unwanted_items_count: number;
};

export type GeoFeatureAddressFavorite = {
  type: string;
  geometry: Geometry;
  properties?: {
    address: AddressType;
    favorite: boolean;
  };
};

export type Geometry = {
  type: string;
  coordinates: Array<number>;
};

export type PropertyDetailsProps = {
  pool: string[];
  last_mls_id: string;
  year_built: number;
  estimated_dollar_per_sqft: number;
  estimated_value: number;
  tax_assessment: number;
  // The "Tax Value" shown in the Decision Making Valuations table
  assessed_value?: number | null;
  status: string;
  sqft: number;
  bedrooms: number;
  bathrooms: number;
  stories: number;
  lot_size_acres: number;
};

export type soldAvmResultProps = {
  estimated_dollar_per_sqft: number;
  estimated_value: number;
  result: {
    // this seems ridiculous. refactor when possible
    estimated_dollar_per_sqft: number;
    estimated_value: number;
  };
};

export type activeAvmResultProps = {
  estimated_dollar_per_sqft: number;
  estimated_value: number;
  result: {
    // this seems ridiculous. refactor when possible
    estimated_dollar_per_sqft: number;
    estimated_value: number;
  };
};

export enum ValuationsType {
  UserInput = 'userInput',
  ActiveAvm = 'activeAvm',
  InactiveAvm = 'inactiveAvm',
  FifteenComp = '15Comp',
  FifteenCompSqFt = '15CompSqFt',
  SixCompSqFt = '6CompSqFt',
  SixComp = '6Comp',
  TaxValue = 'TaxValue',
}

export enum UserValuationType {
  ValuationAmount = 'Valuation Amount',
  DollarPerSqFt = '$ / Sq Ft',
}

export enum ValuationsTypeText {
  UserInput = 'User Input',
  ActiveAvm = 'REIX Active AVM Estimate',
  InactiveAvm = 'REIX Sold AVM Estimate',
  FifteenComp = '15 Sold Comp Avg.',
  FifteenCompSqFt = '15 Sold Comp $/Sq Ft Avg.',
  SixCompSqFt = '6 Sold Comp $/Sq Ft Avg.',
  SixComp = '6 Sold Comp Avg.',
  TaxValue = 'Tax Value',
}

export type ApproachType = {
  priority_liens_calculation: string;
  purchase_percent_of_value: number;
  sale_cost_percent: number;
  hold_months: number;
  approach_conditions: any[];
  created_at: string;
  fixed_cost_calculation: string;
  id: string;
  name: string;
  updated_at: string;
  user_id: string;
};

export type PropertyDetailsType = {
  id: string;
  tax_id: string | null;
  zillow_url: string | null;
  legal_description: string | null;
  clerk_number: string | null;
  property_class: string;
  county_code: string | null;
  pool: string | null;
  status: string | null;
  number_of_units: number | null;
  created_at: string;
  updated_at: string;
  subdivision: string | null;
  bathrooms: number;
  bedrooms: number;
  garage_spaces: number | null;
  sqft: number;
  assessed_value: number;
  year_built: number;
  last_sync_datetime: string | null;
  stories: number;
  lot_size_acres: number;
  list_price: number | null;
  sale_or_rent: string | null;
  recent_rehab: string | null;
  how_sold: string | null;
  property_listing_id: string | null;
  last_mls_id: string | null;
  data_source: string;
  view_count: number;
  on_market_date: string | null;
  cumulative_days_on_market: number | null;
  last_sold_date: string | null;
  total_tax_amount: number;
  avm_result_id: string;
  list_price_per_sqft: number | null;
  third_party_last_modified_date: string | null;
  last_sold_price: number | null;
  order_by_date_created: string;
  proposed_terms: string | null;
  last_tax_info_sync: string;
  most_recent_property_listing_images: string[];
  address: AddressType;
  property_listings: string[];
  property_favorite_flag: string[];
  favorite_flag_count: number;
  list_sort_property_joins: ListSortPropertyJoins[];
  street_view_photo: {
    image_url: string;
    date: string;
  };
  list_uploads_properties_data: {
    list_upload_id: string;
    property_id: string;
    order_number: number;
    auction_number: string;
  }[];
};

export interface Image {
  image_url: string;
  date?: string;
}

export interface PropertyImage extends Image {
  created_at?: string;
  description?: string;
  id?: string;
  image_height?: number;
  image_width?: number;
  preferred_order: number;
  property_listing_id?: string;
  updated_at?: string;
}

export type RealEstateAgent = {
  id: string;
  third_party_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  agent_office_phone_number: string;
  profile_image_url: string | null;
  office_name: string;
  office_mls_id: string;
  office_mls_source: string;
  office_phone_number: string;
  office_email: string;
  office_website: string;
  office_image_url: string | null;
  listing_updated_at: string;
  created_at: string;
  updated_at: string;
};
